import React, { useMemo } from 'react';

import { Badge, Pressable } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';
import Animated, { FlipInEasyY } from 'react-native-reanimated';

import Picture from 'components/picture';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { ClickEventComponentNames, logButtonClick } from 'state/amplitude';
import { parseStringifiedJSON } from 'utils/parse-string';

import { PriceAndCalories } from './price-and-calories';
import { ProductCardReverse } from './product-card-reverse';
import {
  DEFAULT_PRODUCT_CARD_HEIGHT_DESKTOP,
  DEFAULT_PRODUCT_CARD_HEIGHT_MOBILE,
  PRODUCT_CARD_PADDING,
  PRODUCT_CARD_WIDTH,
  PriceAndCaloriesContainer,
  ProductCardBadge,
  ProductCardBadgeLeft,
  ProductCardContainer,
  ProductCardContent,
  ProductCardFooter,
  ProductCardImageContainer,
  ProductCardName,
  ProductNameContainer,
} from './styled';
import { IProductCardProps } from './types';
import { FLIP_TO_DEFAULT_TIME_MS } from './utils';

export const ProductCard: React.FC<IProductCardProps> = ({
  badgeContent,
  badgeContentLeft,
  isFlippable = true,
  isSelected = false,
  imgObjectFitContain = true,
  onProductPress,
  productProps,
  options,
  onOptionPress,
  selectedOption,
  accessibilityRole = 'button',
  accessibilitySelected = false,
}) => {
  const { formatMessage } = useIntl();

  const isMobile = useIsMobileBreakpoint();

  const [flipped, flip] = React.useState<boolean>(false);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout>>(null);
  const wasFlipped = React.useRef(false);

  const isMounted = React.useRef<boolean>(false);

  const { name, image, fallbackImage, loading, price, calories } = productProps;

  const shouldFlip = useMemo(() => {
    return isFlippable && options?.length && !!onOptionPress;
  }, [isFlippable, onOptionPress, options?.length]);

  const handlePress = React.useCallback(() => {
    if (shouldFlip) {
      wasFlipped.current = true;
      timeoutRef.current = setTimeout(() => {
        return flip(false);
      }, FLIP_TO_DEFAULT_TIME_MS);

      logButtonClick({
        attributes: {
          component: ClickEventComponentNames.PRODUCT_CARD_FLIP,
          name: name ?? '',
        },
      });

      return flip(true);
    }

    return onProductPress();
  }, [name, onProductPress, shouldFlip]);

  React.useEffect(() => {
    if (!flipped && wasFlipped.current) {
      const timer = setTimeout(() => {
        wasFlipped.current = false;
      }, FLIP_TO_DEFAULT_TIME_MS);

      return () => clearTimeout(timer);
    }

    return;
  }, [flipped]);

  const handleOptionPress = React.useCallback(
    (value: string) => {
      onOptionPress?.(value);
      // Flips back and resets the waiting duration time.
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        flip(false);
      }
    },
    [onOptionPress]
  );

  const selection = React.useMemo(
    () => (typeof selectedOption === 'string' ? selectedOption : selectedOption?._key ?? ''),
    [selectedOption]
  );

  // Marks the component as Mounted.
  React.useLayoutEffect(() => {
    isMounted.current = true;
  });

  const badge = React.useMemo(() => {
    return badgeContent ? (
      typeof badgeContent === 'string' ? (
        <Badge variant="default-solid">{badgeContent}</Badge>
      ) : (
        <>{badgeContent}</>
      )
    ) : null;
  }, [badgeContent]);

  const badgeLeft = React.useMemo(() => {
    if (!badgeContentLeft) {
      return null;
    }

    if (typeof badgeContentLeft === 'string') {
      return <Badge variant="default-solid">{badgeContentLeft}</Badge>;
    }

    return <>{badgeContentLeft}</>;
  }, [badgeContentLeft]);

  if (!name && !loading) {
    return null;
  }

  const defaultProductCardHeight = isMobile
    ? DEFAULT_PRODUCT_CARD_HEIGHT_MOBILE
    : DEFAULT_PRODUCT_CARD_HEIGHT_DESKTOP;

  const productName = name?.trim();

  return (
    <>
      {flipped && shouldFlip ? (
        <ProductCardReverse
          isSelected={isSelected}
          selection={selection}
          options={options ?? []}
          onOptionPress={handleOptionPress}
          height={defaultProductCardHeight}
        />
      ) : (
        <Pressable
          onPress={handlePress}
          width={PRODUCT_CARD_WIDTH}
          padding={PRODUCT_CARD_PADDING}
          key="product-card-animation"
          accessibilityRole={accessibilityRole}
          {...(accessibilityRole === 'radio' && {
            accessibilityChecked: accessibilitySelected,
          })}
        >
          <Animated.View entering={wasFlipped.current ? FlipInEasyY : undefined}>
            <ProductCardContainer
              $isSelected={isSelected}
              height={defaultProductCardHeight}
              testID="product-card-container"
            >
              <ProductCardBadgeLeft testID="product-card-badge-left">
                {badgeLeft}
              </ProductCardBadgeLeft>
              <ProductCardBadge testID="product-card-badge">{badge}</ProductCardBadge>

              <ProductCardContent>
                {(image || fallbackImage) && (
                  <ProductCardImageContainer key={productName} testID="product-image">
                    {image && (
                      <Picture
                        width="full"
                        flex={1}
                        image={parseStringifiedJSON({ value: image })}
                        alt={`${formatMessage({ id: 'product' })} ${productName}`}
                        accessibilityHidden
                        objectFitContain={imgObjectFitContain}
                      />
                    )}
                    {!image &&
                      fallbackImage &&
                      fallbackImage({
                        width: 55,
                        height: 55,
                      })}
                  </ProductCardImageContainer>
                )}
              </ProductCardContent>

              <ProductCardFooter testID="product-card-footer">
                <ProductNameContainer>
                  <ProductCardName>{productName}</ProductCardName>
                </ProductNameContainer>

                <PriceAndCaloriesContainer testID="product-card-price-and-calories">
                  <PriceAndCalories
                    price={price}
                    calories={calories}
                    showPriceUpcharge={productProps.showPriceUpcharge}
                  />
                </PriceAndCaloriesContainer>
              </ProductCardFooter>
            </ProductCardContainer>
          </Animated.View>
        </Pressable>
      )}
    </>
  );
};
